import React, { useState } from 'react';
import { XIcon, HelpCircleIcon } from 'lucide-react';
import { format } from 'date-fns';
interface SingleTicketEntryModalProps {
  onClose: () => void;
  groupId: string;
  onSave: (ticketData: any) => void;
}
const SingleTicketEntryModal: React.FC<SingleTicketEntryModalProps> = ({
  onClose,
  groupId,
  onSave
}) => {
  const [ticketType, setTicketType] = useState('ticket'); // 'ticket' or 'atc'
  const [ticketData, setTicketData] = useState({
    ticketNumber: '',
    paxName: '',
    sector: '',
    sectorCode: '',
    airlineCode: '',
    class: '',
    issueDate: '',
    airFare: '',
    tax: '',
    otherBkgRef: '',
    remark: '',
    // ATC specific fields
    referenceNumber: '',
    tourCode: '',
    staffResponsible: '',
    cancelDate: '',
    cancelStaff: ''
  });
  const [errors, setErrors] = useState({
    ticketNumber: '',
    paxName: '',
    airlineCode: '',
    airFare: '',
    referenceNumber: '',
    tourCode: '',
    staffResponsible: '',
    ticketType: ''
  });
  // Format date in DD MMM YYYY format for display
  const formatDate = dateString => {
    if (!dateString) return '';
    try {
      return format(new Date(dateString), 'dd MMM yyyy');
    } catch (e) {
      return dateString;
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setTicketData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user types
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };
  const handleTicketTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTicketType(e.target.value);
    // Clear ATC specific errors when switching back to regular ticket
    if (e.target.value === 'ticket') {
      setErrors(prev => ({
        ...prev,
        referenceNumber: '',
        tourCode: '',
        staffResponsible: ''
      }));
    }
  };
  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    // Validate ticket type
    if (!ticketType) {
      newErrors.ticketType = 'Please select a ticket type';
      isValid = false;
    }
    // Common validations for both ticket types
    if (!ticketData.ticketNumber) {
      newErrors.ticketNumber = 'Ticket number is required';
      isValid = false;
    } else if (!/^\d{13}$/.test(ticketData.ticketNumber)) {
      newErrors.ticketNumber = 'Ticket number must be 13 digits';
      isValid = false;
    }
    if (!ticketData.paxName) {
      newErrors.paxName = 'Passenger name is required';
      isValid = false;
    }
    if (!ticketData.airlineCode) {
      newErrors.airlineCode = 'Airline code is required';
      isValid = false;
    }
    if (!ticketData.airFare) {
      newErrors.airFare = 'Air fare is required';
      isValid = false;
    } else if (isNaN(parseFloat(ticketData.airFare))) {
      newErrors.airFare = 'Air fare must be a number';
      isValid = false;
    }
    // ATC specific validations
    if (ticketType === 'atc') {
      if (!ticketData.referenceNumber) {
        newErrors.referenceNumber = 'Reference number is required for ATC entries';
        isValid = false;
      }
      if (!ticketData.tourCode) {
        newErrors.tourCode = 'Tour code is required for ATC entries';
        isValid = false;
      }
      if (!ticketData.staffResponsible) {
        newErrors.staffResponsible = 'Staff responsible is required for ATC entries';
        isValid = false;
      }
    }
    setErrors(prev => ({
      ...prev,
      ...newErrors
    }));
    return isValid;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Format data for submission
      const formattedData = {
        ...ticketData,
        airFare: parseFloat(ticketData.airFare),
        tax: ticketData.tax ? parseFloat(ticketData.tax) : 0,
        totalCost: parseFloat(ticketData.airFare) + (ticketData.tax ? parseFloat(ticketData.tax) : 0),
        type: ticketType === 'atc' ? 'ATC' : undefined
      };
      onSave(formattedData);
      onClose();
    }
  };
  return <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50 flex items-center justify-center" data-id="element-321">
      <div className="relative bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4" data-id="element-322">
        <div className="flex items-start justify-between p-4 border-b border-gray-200" data-id="element-323">
          <h3 className="text-xl font-semibold text-gray-900" data-id="element-324">
            Add {ticketType === 'atc' ? 'ATC Entry' : 'Single Ticket'} - Group{' '}
            {groupId}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500 focus:outline-none" aria-label="Close" data-id="element-325">
            <XIcon className="h-6 w-6" data-id="element-326" />
          </button>
        </div>
        <form onSubmit={handleSubmit} data-id="element-327">
          <div className="p-6 space-y-6" data-id="element-328">
            <div className="mb-4" data-id="element-329">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-330">
                Entry Type <span className="text-red-500" data-id="element-331">*</span>
              </label>
              <div className="flex flex-col space-y-2" data-id="element-332">
                <div className="flex items-center" data-id="element-333">
                  <input type="radio" id="ticket-type" name="ticketType" value="ticket" checked={ticketType === 'ticket'} onChange={handleTicketTypeChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" data-id="element-334" />
                  <label htmlFor="ticket-type" className="ml-2 block text-sm text-gray-900" data-id="element-335">
                    Ticket (Original ticket issuance)
                  </label>
                </div>
                <div className="flex items-center" data-id="element-336">
                  <input type="radio" id="atc-type" name="ticketType" value="atc" checked={ticketType === 'atc'} onChange={handleTicketTypeChange} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300" data-id="element-337" />
                  <label htmlFor="atc-type" className="ml-2 block text-sm text-gray-900" data-id="element-338">
                    ATC (Automated Ticket Change)
                  </label>
                </div>
              </div>
              <div className="mt-2 flex items-center text-sm text-gray-500" data-id="element-339">
                <HelpCircleIcon className="h-4 w-4 mr-1" data-id="element-340" />
                <span data-id="element-341">
                  {ticketType === 'ticket' ? 'Select "Ticket" for new/original ticket issuance' : 'Select "ATC" for ticket reissue or changes (date change, reroute, class change, etc.)'}
                </span>
              </div>
              {errors.ticketType && <p className="mt-1 text-sm text-red-600" data-id="element-342">{errors.ticketType}</p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-343">
              <div data-id="element-344">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-345">
                  Ticket Number <span className="text-red-500" data-id="element-346">*</span>
                </label>
                <input type="text" name="ticketNumber" value={ticketData.ticketNumber} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.ticketNumber ? 'border-red-300 ring-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} placeholder="e.g. 1601234567890" data-id="element-347" />
                {errors.ticketNumber && <p className="mt-1 text-sm text-red-600" data-id="element-348">
                    {errors.ticketNumber}
                  </p>}
                <p className="mt-1 text-xs text-gray-500" data-id="element-349">
                  Enter a 13-digit ticket number
                </p>
              </div>
              <div data-id="element-350">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-351">
                  Passenger Name <span className="text-red-500" data-id="element-352">*</span>
                </label>
                <input type="text" name="paxName" value={ticketData.paxName} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.paxName ? 'border-red-300 ring-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} placeholder="e.g. LOH/POHLEE MS" data-id="element-353" />
                {errors.paxName && <p className="mt-1 text-sm text-red-600" data-id="element-354">{errors.paxName}</p>}
              </div>
              <div data-id="element-355">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-356">
                  Sector Code
                </label>
                <input type="text" name="sectorCode" value={ticketData.sectorCode} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g. SINCAI" data-id="element-357" />
              </div>
              <div data-id="element-358">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-359">
                  Sector
                </label>
                <input type="text" name="sector" value={ticketData.sector} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g. SIN/CAI/SIN" data-id="element-360" />
              </div>
              <div data-id="element-361">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-362">
                  Airline Code <span className="text-red-500" data-id="element-363">*</span>
                </label>
                <input type="text" name="airlineCode" value={ticketData.airlineCode} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.airlineCode ? 'border-red-300 ring-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} placeholder="e.g. QR" data-id="element-364" />
                {errors.airlineCode && <p className="mt-1 text-sm text-red-600" data-id="element-365">
                    {errors.airlineCode}
                  </p>}
              </div>
              <div data-id="element-366">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-367">
                  Class
                </label>
                <input type="text" name="class" value={ticketData.class} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g. G" data-id="element-368" />
              </div>
              <div data-id="element-369">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-370">
                  Issue Date
                </label>
                <input type="date" name="issueDate" value={ticketData.issueDate} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-371" />
              </div>
              <div data-id="element-372">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-373">
                  Other Booking Ref
                </label>
                <input type="text" name="otherBkgRef" value={ticketData.otherBkgRef} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="e.g. QGDCIP" data-id="element-374" />
              </div>
              <div data-id="element-375">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-376">
                  Air Fare <span className="text-red-500" data-id="element-377">*</span>
                </label>
                <div className="relative" data-id="element-378">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-379">
                    <span className="text-gray-500 sm:text-sm" data-id="element-380">$</span>
                  </div>
                  <input type="text" name="airFare" value={ticketData.airFare} onChange={handleChange} className={`mt-1 block w-full pl-7 pr-3 py-2 border ${errors.airFare ? 'border-red-300 ring-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} placeholder="0.00" data-id="element-381" />
                </div>
                {errors.airFare && <p className="mt-1 text-sm text-red-600" data-id="element-382">{errors.airFare}</p>}
              </div>
              <div data-id="element-383">
                <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-384">
                  Tax
                </label>
                <div className="relative" data-id="element-385">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-386">
                    <span className="text-gray-500 sm:text-sm" data-id="element-387">$</span>
                  </div>
                  <input type="text" name="tax" value={ticketData.tax} onChange={handleChange} className="mt-1 block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="0.00" data-id="element-388" />
                </div>
              </div>
            </div>
            {/* ATC specific fields */}
            {ticketType === 'atc' && <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-389">
                <h4 className="text-lg font-medium text-gray-900 mb-4" data-id="element-390">
                  ATC Details
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-391">
                  <div data-id="element-392">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-393">
                      Reference Number <span className="text-red-500" data-id="element-394">*</span>
                    </label>
                    <input type="text" name="referenceNumber" value={ticketData.referenceNumber} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.referenceNumber ? 'border-red-300 ring-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} placeholder="e.g. 12XCAI1207" data-id="element-395" />
                    {errors.referenceNumber && <p className="mt-1 text-sm text-red-600" data-id="element-396">
                        {errors.referenceNumber}
                      </p>}
                  </div>
                  <div data-id="element-397">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-398">
                      Tour Code <span className="text-red-500" data-id="element-399">*</span>
                    </label>
                    <input type="text" name="tourCode" value={ticketData.tourCode} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.tourCode ? 'border-red-300 ring-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} placeholder="e.g. 12XCAI1207/23QR" data-id="element-400" />
                    {errors.tourCode && <p className="mt-1 text-sm text-red-600" data-id="element-401">
                        {errors.tourCode}
                      </p>}
                  </div>
                  <div data-id="element-402">
                    <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-403">
                      Staff Responsible <span className="text-red-500" data-id="element-404">*</span>
                    </label>
                    <select name="staffResponsible" value={ticketData.staffResponsible} onChange={handleChange} className={`mt-1 block w-full px-3 py-2 border ${errors.staffResponsible ? 'border-red-300 ring-red-500' : 'border-gray-300'} rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`} data-id="element-405">
                      <option value="" data-id="element-406">Select staff member</option>
                      <option value="John Doe" data-id="element-407">John Doe</option>
                      <option value="Jane Smith" data-id="element-408">Jane Smith</option>
                      <option value="Michael Johnson" data-id="element-409">Michael Johnson</option>
                      <option value="Sarah Williams" data-id="element-410">Sarah Williams</option>
                    </select>
                    {errors.staffResponsible && <p className="mt-1 text-sm text-red-600" data-id="element-411">
                        {errors.staffResponsible}
                      </p>}
                  </div>
                </div>
                <div className="mt-6" data-id="element-412">
                  <h5 className="text-sm font-medium text-gray-700 mb-2" data-id="element-413">
                    Optional Cancel Information
                  </h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-414">
                    <div data-id="element-415">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-416">
                        Cancel Date
                      </label>
                      <input type="date" name="cancelDate" value={ticketData.cancelDate} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-417" />
                    </div>
                    <div data-id="element-418">
                      <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-419">
                        Cancel Staff
                      </label>
                      <select name="cancelStaff" value={ticketData.cancelStaff} onChange={handleChange} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" data-id="element-420">
                        <option value="" data-id="element-421">Select staff member</option>
                        <option value="John Doe" data-id="element-422">John Doe</option>
                        <option value="Jane Smith" data-id="element-423">Jane Smith</option>
                        <option value="Michael Johnson" data-id="element-424">Michael Johnson</option>
                        <option value="Sarah Williams" data-id="element-425">Sarah Williams</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>}
            <div className={ticketType === 'atc' ? 'mt-6' : 'md:col-span-2'} data-id="element-426">
              <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-427">
                Remarks
              </label>
              <textarea name="remark" value={ticketData.remark} onChange={handleChange} rows={3} className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder={ticketType === 'atc' ? 'Description of changes made (optional)' : 'Additional details about this ticket'} data-id="element-428" />
            </div>
          </div>
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-gray-200" data-id="element-429">
            <button type="submit" className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-430">
              {ticketType === 'atc' ? 'Entry Complete' : 'Save Ticket'}
            </button>
            <button type="button" onClick={onClose} className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm" data-id="element-431">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>;
};
export default SingleTicketEntryModal;